import { lazy, Suspense, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Box, LayoutGrid, Map } from "lucide-react";
import { ROOMS, START, roomById, useRooms } from "@/data/rooms";
import { FloorPlan } from "@/components/explore/FloorPlan";
import { RoomPanel } from "@/components/explore/RoomPanel";
import { MaskLines } from "@/components/MaskLines";
import { useLang } from "@/lib/i18n";

const Museum3D = lazy(() => import("@/components/explore/Museum3D"));

const ModeSwitch = ({ mode, setMode, className = "relative", compact = false }) => {
  const { t } = useLang();
  return (
  <div className={`flex rounded-full border border-white/15 bg-white/5 p-1 ${className}`} data-testid="mode-switch">
    {[["2d", t("explore.mode2d"), Map], ["3d", t("nav.cta3d"), Box]].map(([id, label, Icon]) => (
      <button key={id} onClick={() => setMode(id)} className="relative z-10 flex items-center gap-2 px-4 py-2 text-sm" data-testid={`mode-${id}`}>
        {mode === id && <motion.span layoutId="mode-pill" className="absolute inset-0 -z-10 rounded-full bg-[var(--ink)]" transition={{ type: "spring", stiffness: 380, damping: 32 }} />}
        <span className={mode === id ? "text-[var(--bg)]" : "text-[var(--ink)]"}><Icon size={15} className="inline" /> <span className={compact ? "hidden sm:inline" : ""}>{label}</span></span>
      </button>
    ))}
  </div>
  );
};

const RoomChips = ({ selected, onPick }) => (
  <div className="mt-6 flex gap-2 overflow-x-auto pb-2" data-testid="room-chips">
    {useRooms().map((r) => (
      <button key={r.id} onClick={() => onPick(r.id)} className={`chip ${selected === r.id ? "chip-on" : ""}`} style={{ "--c": r.color }} data-testid={`chip-${r.id}`}>
        <span className="mono text-[11px] opacity-70">{r.num}</span> {r.name}
      </button>
    ))}
  </div>
);

const RoomRail = ({ selected, onPick }) => (
  <nav className="absolute left-4 top-[72px] z-20 flex flex-col items-start gap-2" data-testid="room-rail">
    {useRooms().map((r) => (
      <button key={r.id} onClick={() => onPick(r.id)} className={`rail-btn ${selected === r.id ? "rail-on" : ""}`} style={{ "--c": r.color }} data-testid={`chip-${r.id}`}>
        <span className="rail-dot" />
        <span className="mono text-[11px] opacity-70">{r.num}</span>
        <span className="hidden sm:inline">{r.name}</span>
      </button>
    ))}
  </nav>
);

export default function Explore() {
  const { t } = useLang();
  const [params, setParams] = useSearchParams();
  const mode = params.get("mode") === "3d" ? "3d" : "2d";
  const initial = roomById(params.get("sala"));
  const [selected, setSelected] = useState(initial?.id ?? "social");
  const [target, setTarget] = useState(initial ? { ...initial.view } : START);
  const [panel, setPanel] = useState(false);
  const room = roomById(selected) ?? ROOMS[0];

  const setMode = (m) => setParams((p) => { const n = new URLSearchParams(p); n.set("mode", m); return n; }, { replace: true });
  const enter3D = (id) => { setSelected(id); setTarget({ ...roomById(id).view, n: Date.now() }); setPanel(false); setMode("3d"); };
  const pick = (id) => (mode === "3d" ? enter3D(id) : setSelected(id));

  if (mode === "3d") {
    return (
      <main className="pt-[72px]" data-testid="explore-page">
        <div className="relative h-[calc(100svh-72px)] min-h-[480px] w-full">
          <Suspense fallback={<div className="h-full w-full animate-pulse bg-white/5" />}>
            <Museum3D target={target} onPin={(id) => { setSelected(id); setPanel(true); }} />
          </Suspense>
          <ModeSwitch compact mode={mode} setMode={setMode} className="absolute bottom-6 right-3 z-20 bg-black/70 backdrop-blur-md sm:bottom-auto sm:left-1/2 sm:right-auto sm:top-3 sm:-translate-x-1/2" />
          <RoomRail selected={selected} onPick={pick} />
          {panel && (
            <div className="absolute inset-x-3 bottom-3 z-30 sm:inset-x-auto sm:bottom-auto sm:right-4 sm:top-28 sm:w-[340px]">
              <RoomPanel room={room} onClose={() => setPanel(false)} className="max-h-[70svh] overflow-y-auto" />
            </div>
          )}
        </div>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-[1440px] px-5 pb-24 pt-28 md:px-10" data-testid="explore-page">
      <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="mono text-xs text-[var(--brick-light)]">{t("explore.kicker")}</p>
          <MaskLines as="h1" className="mt-3 font-display text-5xl leading-[0.95] sm:text-6xl lg:text-7xl" lines={[t("explore.l1"), <em key="e">{t("explore.l2")}</em>]} />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Link to="/salas" className="btn-ghost !py-2" data-testid="explore-all-rooms-link"><LayoutGrid size={15} /> {t("explore.allRooms")}</Link>
          <ModeSwitch mode={mode} setMode={setMode} />
        </div>
      </div>
      <RoomChips selected={selected} onPick={pick} />

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="min-w-0 rounded-2xl border border-white/10 bg-[var(--card)] p-4 md:p-8">
          <p className="mono mb-3 text-[11px] text-[var(--muted)] md:hidden">{t("explore.swipe")}</p>
          <div className="overflow-x-auto" data-lenis-prevent>
            <div className="min-w-[1000px] lg:min-w-0"><FloorPlan selected={selected} onSelect={setSelected} /></div>
          </div>
          <div className="mt-8 overflow-hidden rounded-xl">
            <img src="/img/topdown.webp" alt={t("over.alt")} className="w-full object-cover" />
          </div>
          <p className="mono mt-3 text-[11px] text-[var(--muted)]">{t("explore.caption")}</p>
        </div>
        <RoomPanel room={room} onEnter3D={enter3D} />
      </div>
    </main>
  );
}
